/* KCH build 25JUL2026-b | kch-reviews.js */
/* ============================================================================
   Kitty Cat Habitat, Reviews / Testimonials carousel engine
   Asset: kch-reviews.js   |   Concept theme (RoarTheme)

   Standalone coverflow for the review section. Data-attribute driven with its
   own namespace ([data-kch-reviews], .kch-review-card, data-kchr-*) so it never
   collides with the product carousel or double-initialises.

   Quick view, add to cart and choose options all run on Concept's own
   components (aria-controls opens the theme <quick-view>, and the theme product
   form plus kch-custom.js drive the buy button), so this file only drives the
   motion, the pointer drag, and the screen-reader status. It touches no theme
   file.
   ============================================================================ */
(function () {
  'use strict';

  /* Focus intent, once per page and outside any section init. Earlier versions attached
     this inside the engine, which returns early in Concept mode, so Concept had no
     suppression at all: that is why the ring kept coming back. The flag lives on the
     document element, is set by any pointer press, and clears only on Tab, because Tab
     is the signal that someone is navigating by keyboard and needs to see focus. */
  if (!window.__kchFocusIntent) {
    window.__kchFocusIntent = true;
    document.addEventListener('pointerdown', function () {
      document.documentElement.setAttribute('data-kch-pointer', '');
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab') document.documentElement.removeAttribute('data-kch-pointer');
    }, true);
  }


  if (window.__kchrLoaded) return;
  window.__kchrLoaded = true;


  /* Mobile effect, scroll driven. Same rule as the carousel, same reasoning: the
     card keeps concept's size and the effect lives in the travel between cards.
     Native scroll owns position so snap and momentum stay the browser's; the
     transform is derived from where each card already sits. No horizontal term,
     it would fight the scroll. y is signed so one neighbour drops and one lifts.
     Geometry comes from custom properties on the root. 25 Jul. */
  function kchrMobileEffect(root, track) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce && reduce.matches) return;
    var cards = Array.prototype.slice.call(track.querySelectorAll('.kch-review-card'));
    if (cards.length < 2) return;
    var cs = getComputedStyle(root);
    function num(name, fb) { var v = parseFloat(cs.getPropertyValue(name)); return isNaN(v) ? fb : v; }
    var persp = num('--kchr-m-persp', 900);
    var g = { spread: num('--kchr-m-spread', 0), radius: num('--kchr-m-radius', 520),
              tilt: num('--kchr-m-tilt', 0), rise: num('--kchr-m-rise', 0),
              fade: num('--kchr-m-fade', 1) };
    if (!g.spread && !g.tilt && !g.rise && g.fade === 1) return;
    var RAD = Math.PI / 180, raf = 0;
    function paint() {
      raf = 0;
      /* Layout geometry, never painted geometry. 26 Jul.

         This measured with getBoundingClientRect, which returns the box after
         transforms. Every card here carries a rotateY and a Z translation, so from the
         second pass onward the function was measuring its own output: the gap between
         cards 0 and 1 read 23.4px against a real 12px, step came out 312 against a real
         301, and every d was wrong by up to 0.08. Run repeatedly at rest it converged
         after three passes, but with the scroll also moving it never converged, it
         chased itself. That feedback is the uncontrolled motion.

         offsetLeft and offsetWidth are layout values and are not affected by transform,
         so the same reading comes back no matter what has already been applied. The
         desktop engine is immune for the same reason: it derives d from the card index
         rather than from anything it measures. */
      var step = cards.length > 1 ? (cards[1].offsetLeft - cards[0].offsetLeft) : cards[0].offsetWidth;
      if (!step) return;
      var origin = cards[0].offsetLeft;
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i];
        /* distance from the resting card, in whole card steps, straight from layout */
        var d = ((c.offsetLeft - origin) - track.scrollLeft) / step;
        var dc = Math.max(-2, Math.min(2, d));
        var theta = dc * g.spread * RAD;
        var z = (Math.cos(theta) - 1) * g.radius;
        var y = dc * g.rise, tilt = dc * g.tilt;
        var a = Math.min(1, Math.abs(dc));
        /* perspective() first, per card, rather than on the track. The track is the
           scroll container, so a container perspective pinned the vanishing point to
           its own visible box and every card re-projected against it on each scroll
           frame, swinging the whole field with the finger. Per card the vanishing point
           is centred on the card itself, so depth and rotation read the same wherever
           it sits in the row and scrolling only moves cards. 26 Jul. */
        c.style.transform = 'perspective(' + persp.toFixed(0) + 'px) translate3d(0,' + y.toFixed(1) + 'px,' + z.toFixed(1) + 'px) rotateY(' + tilt.toFixed(2) + 'deg)';
        /* Cull past the clamp instead of stacking there. dc is clamped to +/-2, so every
           card beyond two steps got identical transforms and sat at the same fade,
           piling up as a smear behind the row. The desktop engine culls the equivalent
           cards to zero; this matches it so the two read alike. Sean 26 Jul: the mobile
           fan looked nothing like the desktop one. 26 Jul. */
        var culled = Math.abs(d) > 2.2;
        c.style.opacity = culled ? '0' : (1 - (1 - g.fade) * a).toFixed(3);
        c.style.pointerEvents = culled ? 'none' : '';
        c.style.zIndex = String(100 - Math.round(a * 100));
      }
    }
    /* Paint on settle, not on every scroll frame. Sean 26 Jul: dragging made the whole
       row wheel around, uncontrolled, as if it were off its tracks.

       The cause is that the two halves ran on different threads. A native scroller is
       driven on the compositor, while this repainted from a scroll listener through
       requestAnimationFrame on the main thread, and will-change put every card on its
       own layer. Through a momentum drag the transforms lagged the real scroll position
       by a frame or more, so each card swam against the row instead of travelling with
       it. Desktop never showed this because it animates between discrete stops with
       nothing else moving the cards.

       So the drag is now left entirely to the browser, which is always on rails, and
       the fan is rebuilt once the scroll comes to rest. Cards keep their last transform
       while the row moves natively, then ease into the new arrangement through the
       transition added to the card rule in kch-reviews.css. Nothing competes with the
       scroll at any point, so there is nothing left to desync. 26 Jul. */
    var settle = 0;
    function repaint() { if (!raf) raf = requestAnimationFrame(paint); }
    function onSettle() { clearTimeout(settle); settle = setTimeout(repaint, 90); }
    /* scrollend fires exactly at the snap where the browser supports it; the debounce
       covers everything else and is harmless alongside it. */
    if ('onscrollend' in window) track.addEventListener('scrollend', repaint);
    track.addEventListener('scroll', onSettle, { passive: true });
    window.addEventListener('resize', onSettle);
    root.classList.add('kchr-m-effect');
    paint();
  }

  /* Card custody. Exactly one of Concept's slider and the engine's track holds the
     cards at any moment, never both. That is the whole point: the slider is a live
     custom element that tracks its own items and computes its own step, so sharing
     the cards with the engine would put two owners on one set of positions, which
     is the fault this replaces.

     appendChild moves nodes, it does not clone them, so nothing is duplicated, no
     image is fetched again and listeners survive. Both are safe to call twice.
     27 Jul. */
  function kchAdoptCards(root) {
    const track = root.querySelector('[data-kchr-track]');
    const home = root.querySelector('[data-kchr-home]');
    if (!track || !home) return;
    const moving = home.querySelectorAll('.kch-review-card');
    moving.forEach((c) => track.appendChild(c));
    if (moving.length) root.classList.add('kch-reviews--engine');
  }

  function kchReturnCards(root) {
    const track = root.querySelector('[data-kchr-track]');
    const home = root.querySelector('[data-kchr-home]');
    if (!track || !home) return;
    const moving = track.querySelectorAll('.kch-review-card');
    moving.forEach((c) => home.appendChild(c));
    root.classList.remove('kch-reviews--engine');
  }

  function initReviews(root) {
    if (root.__kchrInit) return;

    var stage = root.querySelector('[data-kchr-stage]');
    var track = root.querySelector('[data-kchr-track]');

  /* Concept mode scrolls a real element, and the browser's own smooth scroll gives no
     control over duration or curve: on a short pitch it lands almost instantly, which is
     the snap Sean is describing. This animates scrollLeft directly with an ease-out, so
     the distance always takes the same comfortable time whatever the card width. Any new
     glide cancels the one in flight, so arrow mashing does not fight itself. */
  function kchrGlide(el, to, ms) {
    if (el.__kchrGlide) cancelAnimationFrame(el.__kchrGlide);
    var from = el.scrollLeft;
    var delta = to - from;
    if (Math.abs(delta) < 1) { el.scrollLeft = to; return; }
    var t0 = null;
    var ease = function (t) { return 1 - Math.pow(1 - t, 3); };
    var step = function (ts) {
      if (t0 === null) t0 = ts;
      var t = Math.min(1, (ts - t0) / ms);
      el.scrollLeft = from + delta * ease(t);
      if (t < 1) { el.__kchrGlide = requestAnimationFrame(step); }
      else { el.__kchrGlide = null; }
    };
    el.__kchrGlide = requestAnimationFrame(step);
  }

  /* Concept-mode desktop drag: the stock slider-element has no pointer drag at all,
     verified in theme.js (buttons and native scroll only). This adds click-drag-to-
     scroll for mouse users on the concept slider, mirroring the hero's proven
     pointer-capture pattern. Touch stays native; snapping re-engages on release. */
  /* The arrows in concept mode are Concept's own slider buttons, which scroll with the
     browser's smooth behaviour. This intercepts them in the capture phase, before the
     theme's handler runs, and glides one card pitch at our own pace instead. Falls back
     to the theme's behaviour if the pitch cannot be read. */
  function kchrConceptArrows(root) {
    var wrap = root.querySelector('.kch-concept');
    if (!wrap || wrap.__kchrArrows) return;
    wrap.__kchrArrows = true;
    wrap.addEventListener('click', function (e) {
      var btn = e.target.closest('.kch-arrow, [is="previous-button"], [is="next-button"]');
      if (!btn) return;
      /* a11y 22 Oct 2026 (R5-1): an arrow at the end is aria-disabled and stays focusable */
      if (btn.getAttribute('aria-disabled') === 'true') { e.preventDefault(); e.stopPropagation(); return; }
      var sl = wrap.querySelector('slider-element');
      var grid = sl && sl.querySelector('.card-grid');
      var items = grid ? Array.prototype.slice.call(grid.children) : [];
      if (!sl || items.length < 2) return;
      var base = items[0].offsetLeft;
      var pitch = items[1].offsetLeft - base;
      if (!(pitch > 0)) return;
      e.preventDefault();
      e.stopPropagation();
      var dir = btn.matches('.kch-arrow--prev, [is="previous-button"]') ? -1 : 1;
      var max = sl.scrollWidth - sl.clientWidth;
      var current = base + Math.round((sl.scrollLeft - base) / pitch) * pitch;
      kchrGlide(sl, Math.max(0, Math.min(current + dir * pitch, max)), 620);
    }, true);
  }

  function kchConceptDrag(root) {
    const sl = root.querySelector('.kch-concept slider-element');
    if (!sl || sl.__kchDrag) return;
    sl.__kchDrag = true;
    let startX = 0, startLeft = 0, moved = 0, active = false;
    let dragging = false, suppressClick = false;
    sl.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      active = true; dragging = false; moved = 0; startX = e.clientX; startLeft = sl.scrollLeft;
      /* no capture yet: claiming the pointer here retargeted every click to the
         slider and killed the card links, Sean's 23 Jul dead-button report. The
         pointer is only captured once real movement makes this a drag. */
    });
    let lastX = 0, lastT = 0, vel = 0;
    sl.addEventListener('pointermove', (e) => {
      if (!active) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      if (!dragging) {
        if (Math.abs(dx) <= 4) return;
        dragging = true;
        sl.classList.add('kch-dragging');
        sl.setPointerCapture(e.pointerId);
      }
      const now = performance.now();
      if (lastT) {
        const dt = now - lastT;
        if (dt > 0) vel = (e.clientX - lastX) / dt;
      }
      lastX = e.clientX; lastT = now;
      sl.scrollLeft = startLeft - dx;
    });
    const end = (e) => {
      if (!active) return;
      active = false;
      if (!dragging) { vel = 0; lastT = 0; return; }
      dragging = false; suppressClick = true;
      sl.classList.remove('kch-dragging');
      if (sl.hasPointerCapture && sl.hasPointerCapture(e.pointerId)) sl.releasePointerCapture(e.pointerId);
      /* momentum glide, then settle exactly on the card pitch: base plus a whole
         number of pitches is origin-proof, unlike raw offsetLeft comparisons. */
      const projected = sl.scrollLeft - vel * 160;
      const grid = sl.querySelector('.card-grid');
      const items = grid ? Array.from(grid.children) : [];
      if (items.length > 1) {
        const base = items[0].offsetLeft;
        const pitch = items[1].offsetLeft - base;
        if (pitch > 0) {
          const target = base + Math.round((projected - base) / pitch) * pitch;
          const max = sl.scrollWidth - sl.clientWidth;
          /* longer than a snap and eased out, so the release settles rather than jumps */
          kchrGlide(sl, Math.max(0, Math.min(target, max)), 620);
        }
      }
      vel = 0; lastT = 0;
    };
    sl.addEventListener('pointerup', end);
    sl.addEventListener('pointercancel', end);
    sl.addEventListener('click', (e) => {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false; }
    }, true);
    sl.addEventListener('dragstart', (e) => e.preventDefault());
  }
  /* a11y 22 Oct 2026 (R5-1), Sean's keyboard test: the WAI-ARIA APG carousel pattern for
     concept mode. Before this the section cost 41 Tab stops on the home page (Next, then the
     quick-view eye and View Product of all 20 cards), and every stop made Chrome scroll the
     snap row to that control, so the row jumped back and forth.

     Now the section costs three stops: Previous, Next, and one stop for the review area.
     The cards are a roving tabindex: one card is tabindex 0, the rest -1, and their own
     controls (eye, View Product, photo and name links) are out of the Tab order, so a
     mouse still uses them as before. Left and Right (reversed in RTL) and Home and End
     move between cards; the row glides only when the new card is not fully visible, and
     by just enough to show it. Enter on a card clicks its View Product link, which keeps
     the stop count at three instead of five for an active card with its own two controls.
     While focus is elsewhere the active card follows the row, so Tab always lands on a
     card that is already on screen and nothing scrolls sideways when it does.

     The head arrows use the theme's rule (theme.a11y.setDisabled): an unavailable arrow is
     not a Tab stop, except while it already has focus (R6-3, 22 Oct 2026).

     Concept mode only. Cylinder and spiral run their own engine on desktop with their own
     gating; no template uses them. Keydown from inside a card's quick view, which is a
     child of the card, is ignored because its target is not the card. */
  function kchrRoving(root) {
    if (!root.classList.contains('kch-reviews--concept')) return;
    var sl = root.querySelector('.kch-concept slider-element');
    if (!sl || sl.__kchrRoving) return;
    var list = function () { return Array.prototype.slice.call(sl.querySelectorAll('.card-grid > .kch-review-card')); };
    var all = list();
    if (!all.length) return;
    sl.__kchrRoving = true;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var tpl = sl.getAttribute('data-kchr-status-tpl') || '';
    var hintId = sl.getAttribute('data-kchr-hint');
    if (hintId && !document.getElementById(hintId)) hintId = null;
    var INNER = '.kch-review-card__eye, .kch-review-card__cta, .kch-review-card__photolink, .kch-review-card__product';
    /* a11y R8-1 (22 Oct 2026, Sean): Tab walks through every review: card, its quick view
       (eye), its View Product, then the next card, to the last review, then out of the
       section; Shift+Tab the reverse. The photo and product-name links stay out (they repeat
       View Product). Tab and Shift+Tab inside the row are handled here: focus moves without the browser's
       own scroll and the row glides only if the card is cut off, by the overhang, so it never
       swings back (the round 5 fault). The arrow keys, Home and End still move card to card. */
    var STOPS = '.kch-review-card__eye, .kch-review-card__cta';
    all.forEach(function (card, i) {
      card.setAttribute('tabindex', '0');
      if (tpl) card.setAttribute('aria-label', tpl.replace('[x]', String(i + 1)).replace('[y]', String(all.length)));
      var ids = [];
      var q = card.querySelector('.kch-review-card__quote');
      if (q) { if (!q.id) q.id = (sl.id || 'KchrSlider') + '-quote-' + (i + 1); ids.push(q.id); }
      if (hintId) ids.push(hintId);
      if (ids.length) card.setAttribute('aria-describedby', ids.join(' '));
      Array.prototype.forEach.call(card.querySelectorAll(INNER), function (el) {
        if (el.closest('quick-view')) return;
        el.setAttribute('tabindex', el.matches(STOPS) ? '0' : '-1');
      });
    });

    function shown(el) {
      if (!el.getClientRects().length) return false;
      var cs = getComputedStyle(el);
      return cs.visibility !== 'hidden' && cs.display !== 'none';
    }
    /* the Tab sequence inside the row, in DOM order: card, eye, View Product, next card ... */
    function sequence() {
      var seq = [];
      list().forEach(function (card) {
        if (!shown(card)) return;
        seq.push(card);
        Array.prototype.forEach.call(card.querySelectorAll(STOPS), function (el) {
          if (!el.closest('quick-view') && el.tabIndex >= 0 && shown(el)) seq.push(el);
        });
      });
      return seq;
    }
    /* scroll only when the card is cut off, and only by the overhang; scrollLeft grows
       towards the inline end in both directions in Chrome, so the same sum works in RTL */
    function reveal(card) {
      var a = card.getBoundingClientRect(), s = sl.getBoundingClientRect();
      var delta = 0;
      if (a.left < s.left - 1) delta = a.left - s.left;
      else if (a.right > s.right + 1) delta = a.right - s.right;
      if (!delta) return;
      var to = sl.scrollLeft + delta;
      if (reduce.matches) sl.scrollLeft = to; else kchrGlide(sl, to, 420);
    }
    function moveTo(el) {
      el.focus({ preventScroll: true });
      var card = el.closest('.kch-review-card');
      if (card) reveal(card);
    }

    sl.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.target.closest && e.target.closest('quick-view')) return;
      if (e.key === 'Tab') {
        var seq = sequence();
        var at = seq.indexOf(e.target);
        if (at < 0) return;
        var to = at + (e.shiftKey ? -1 : 1);
        if (to < 0 || to >= seq.length) return; /* leave the row: the browser takes over */
        e.preventDefault();
        moveTo(seq[to]);
        return;
      }
      if (e.shiftKey) return;
      all = list();
      var i = all.indexOf(e.target);
      if (i < 0) return;
      var back = getComputedStyle(sl).direction === 'rtl' ? 1 : -1;
      var n;
      switch (e.key) {
        case 'ArrowLeft': case 'Left': n = i + back; break;
        case 'ArrowRight': case 'Right': n = i - back; break;
        case 'Home': n = 0; break;
        case 'End': n = all.length - 1; break;
        case 'Enter': {
          /* the View Product link first: kch-custom.js turns a click on the photo into quick view */
          var link = e.target.querySelector('a.kch-review-card__cta[href]') || e.target.querySelector('a.kch-review-card__product[href]');
          if (link) { e.preventDefault(); link.click(); }
          return;
        }
        default: return;
      }
      e.preventDefault();
      if (n < 0 || n >= all.length || n === i) return;
      moveTo(all[n]);
    });

    /* focus that comes from outside the row (Tab from the arrows, Shift+Tab from the next
       section, a click): once the browser has scrolled, make the whole card visible */
    sl.addEventListener('focusin', function (e) {
      var card = e.target.closest ? e.target.closest('.kch-review-card') : null;
      if (!card || (e.target.closest && e.target.closest('quick-view'))) return;
      requestAnimationFrame(function () { reveal(card); });
      /* keyboard focus on a card: bring its hanging View Product button on screen too, if
         the page stopped with it below the fold. Vertical only, never past the card top. */
      if (card === e.target && card.matches(':focus-visible')) {
        var r = card.getBoundingClientRect();
        var bottom = r.bottom;
        Array.prototype.forEach.call(card.querySelectorAll('.kch-review-card__cta-wrap'), function (w) {
          var wr = w.getBoundingClientRect(); if (wr.height && wr.bottom > bottom) bottom = wr.bottom;
        });
        var dy = Math.min(bottom - (window.innerHeight - 16), r.top - 96);
        if (dy > 0) window.scrollBy({ top: dy, behavior: reduce.matches ? 'auto' : 'smooth' });
      }
    });

    /* Tab from the head arrows enters the row at the first review on screen (after Next
       has moved the row), not at review 1 off to the side */
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
      if (!e.target.closest || !e.target.closest('.kch-headarrows')) return;
      var cards = list().filter(shown);
      var s = sl.getBoundingClientRect();
      for (var k = 0; k < cards.length; k++) {
        var a = cards[k].getBoundingClientRect();
        if (a.left >= s.left - 2 && a.right <= s.right + 2) {
          if (k === 0) return;
          var later = e.target.closest('.kch-headarrows').querySelectorAll('button:not([disabled])');
          if (later[later.length - 1] !== e.target) return; /* Previous: Tab goes to Next first */
          e.preventDefault();
          moveTo(cards[k]);
          return;
        }
      }
    });

    /* a11y 22 Oct 2026 (R6-3): the head arrows follow the theme rule again
       (theme.a11y.setDisabled): an arrow at the end keeps focus while it has it, announced
       unavailable, and is skipped by Tab once focus leaves. Round 5 kept Previous as a
       greyed Tab stop at the start, which read as "the arrows only go right". */
  }

  kchConceptDrag(root);
  kchrConceptArrows(root);
  kchrRoving(root);


    /* Hover arms the arrow keys. Requiring a click first meant focusing the container,
       and focusing the container is what painted the outline; arrow keys are routed by
       pointer position instead, so nothing takes focus and no ring can appear. Keyboard
       users are unaffected: tabbing to the stage still focuses it and its own handler
       still runs, ring and all. Attached before the engine's early return, so Concept
       mode gets it too, where there is no stage at all. */
    var kchHover = false;
    root.addEventListener('pointerenter', function () { kchHover = true; });
    root.addEventListener('pointerleave', function () { kchHover = false; });
    function kchNav(dir) {
      if (root.__kchNav) { root.__kchNav(dir); return; }
      var sl = root.querySelector('slider-element');
      var first = root.querySelector('.kch-review-card');
      if (!sl) return;
      var step = first ? first.getBoundingClientRect().width + 16 : sl.clientWidth * 0.8;
      sl.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
    /* named so cleanup can remove it. document outlives the section, so an anonymous
       handler here cannot be removed at all and one is left behind per re-render.
       Shopify's guidance for shopify:section:unload is to clean up listeners so no
       memory leaks occur, which an anonymous listener makes impossible.
       https://shopify.dev/docs/storefronts/themes/best-practices/editor/integrate-sections-and-blocks
       27 Jul. */
    const kchKeys = function (e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      /* a11y 22 Oct 2026 (R5-1): a card already handled it (kchrRoving), so do not move twice */
      if (e.defaultPrevented) return;
      var st = root.querySelector('[data-kchr-stage]');
      var focusedInside = st && (document.activeElement === st || st.contains(document.activeElement));
      if (!kchHover && !focusedInside) return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'ArrowLeft' || e.key === 'Left') { e.preventDefault(); kchNav(-1); }
      else if (e.key === 'ArrowRight' || e.key === 'Right') { e.preventDefault(); kchNav(1); }
      else if (e.key === 'Home' && root.__kchHome) { e.preventDefault(); root.__kchHome(); }
      else if (e.key === 'End' && root.__kchEnd) { e.preventDefault(); root.__kchEnd(); }
    };
    document.addEventListener('keydown', kchKeys);
    root.__kchrKeys = kchKeys;

    if (!stage || !track) return;
    /* 23 Jul restructure: the engine is desktop-only. Under 1024px the section runs the
       native swipe row from the stylesheet; if the viewport later crosses up, init then. */
    /* the inline pre-paint normally adopts before first paint; this covers a fresh
       init where it did not, such as crossing up or a section re-render in the editor */
    if (!window.matchMedia('(max-width: 1023px)').matches) kchAdoptCards(root);
    const kchMq = window.matchMedia('(max-width: 1023px)');
    if (kchMq.matches) {
      const kchOnce = (e) => { if (!e.matches) { kchMq.removeEventListener('change', kchOnce); initReviews(root); } };
      kchMq.addEventListener('change', kchOnce);
      /* it removes itself on crossing up, but a phone-width visitor who never crosses
         leaves it bound, and in the editor that is one more per re-render. 27 Jul. */
      root.__kchrCleanup = function () { try { kchMq.removeEventListener('change', kchOnce); } catch (e) {} };
      /* optional mobile arrows: the buttons page the native swipe row by one card */
      if (root.classList.contains('kch-arrows-m')) {
        const prev = root.querySelector('[data-kchr-prev]');
        const next = root.querySelector('[data-kchr-next]');
        const page = (dir) => {
          const card = track.querySelector(':scope > *');
          const step = card ? card.getBoundingClientRect().width + 12 : track.clientWidth * 0.74;
          track.scrollBy({ left: dir * step, behavior: 'smooth' });
        };
        if (prev) prev.addEventListener('click', () => page(-1));
        if (next) next.addEventListener('click', () => page(1));
      }
      /* 27 Jul, Sean's call: cylinder and spiral drop to the plain swipe row below
         1024px, the same presentation concept already uses.

         The fan was never going to work here. The desktop engine owns position: it
         decides where every card sits and animates between fixed stops. This path owns
         nothing. The browser owns position through native scrolling and the effect only
         decorated whatever the scroller had already done, so the two could never agree.
         Cards settled wherever momentum and snap left them, the fan then caught up and
         visibly reshuffled, and vertical rise driven by a horizontal input made the row
         orbit under a finger. Five separate fixes each moved the symptom.

         The effect is also barely legible at this size, so it was buying nothing.
         kchrMobileEffect is left in the file, unreferenced, in case the engine is ever
         brought to mobile properly; nothing calls it and the CSS that keyed off
         .kchr-m-effect has been removed with it. */
      return;
    }

    /* flag set here, after the mobile gate, not at the top of the function. Setting it
       first meant a page that loaded narrow marked itself initialised and then returned,
       so the resize handler's re-init call hit the guard and the engine could never start
       on crossing up to desktop. kch-product-carousel.js already did it this way.

       Deliberately NOT given the carousel's adaptive spread: Sean confirmed reviews in
       cylinder already reads correctly, and the rule would have widened its fan by 2.4
       to 5 degrees at the card widths in use. Nothing changes what is already right. */
    root.__kchrInit = true;

    var cards = Array.prototype.slice.call(track.querySelectorAll('.kch-review-card'));
    if (!cards.length) return;
    /* an overflow:hidden box still scrolls programmatically: a browser focus
       scroll or any scrollIntoView inside the stage shifts stage.scrollLeft and
       the whole coordinate system, arrows included, walks sideways with no way
       back. The engine owes its geometry to transforms alone, so the stage's
       own scroll stays pinned at zero. */
    stage.scrollLeft = 0;
    stage.scrollTop = 0;
    stage.addEventListener('scroll', function () { stage.scrollLeft = 0; stage.scrollTop = 0; });

    var prevBtn = root.querySelector('[data-kchr-prev]');
    var nextBtn = root.querySelector('[data-kchr-next]');

    var p = { spread: -16, radius: 640, tilt: 0, rise: 0, cardW: 300, zflat: 1, originX: 0.5, count: cards.length };
    function readGeometry() {
      var cs = getComputedStyle(root);
      function num(name, fb) { var v = parseFloat(cs.getPropertyValue(name)); return isNaN(v) ? fb : v; }
      p.spread = num('--kchr-spread', p.spread);
      p.radius = num('--kchr-radius', p.radius);
      p.tilt = num('--kchr-tilt', p.tilt);
      p.rise = num('--kchr-rise', p.rise);
      p.cardW = num('--kchr-card-w', p.cardW);
      p.zflat = num('--kchr-zflat', 1);
      /* Space between cards, 25 Jul. This section had no such rule: it used the preset
       * angle verbatim, so the gap between cards was whatever sin(spread) * radius
       * happened to give and moved with the card width setting. The carousel already
       * derived its angle from a clearance ratio, but from a hardcoded 0.92. Both now
       * read the same per-section setting, so the two behave alike and the merchant can
       * open the fan out or close it up. Clearance is a fraction of one card's width:
       * 1.0 has neighbours just touching, below that they overlap. */
      if (p.radius > 0 && p.cardW > 0) {
        /* radius, not angle: see the note in kch-product-carousel.js. Holding the preset
           angle keeps each effect's character and makes the separation track the setting
           linearly instead of wrapping round the cylinder at the top of the range. */
        var sin = Math.abs(Math.sin(p.spread * Math.PI / 180));
        if (sin > 0.01) p.radius = (p.cardW * num('--kchr-clear', 0.92)) / sin;
      }
      p.originX = num('--kchr-origin-x', 50) / 100;
      p.visible = Math.max(3, num('--kchr-visible', 5));
      p.grade = num('--kchr-grade', 1);
      p.start = num('--kchr-start', 0);
      p.wrap = num('--kchr-wrap', 0);
      p.gradek = num('--kchr-gradek', 55) / 100;
      p.jitter = num('--kchr-jitter', 0);
      p.back = num('--kchr-back', 0);
      p.count = cards.length;
    }
    readGeometry();

    /* ---- screen-reader status (localised template, [x]/[y] filled in JS) ---- */
    var statusEl = document.createElement('div');
    statusEl.setAttribute('aria-live', 'polite');
    statusEl.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    root.appendChild(statusEl);
    var statusTpl = root.getAttribute('data-kchr-status-tpl') || 'Review [x] of [y]';

    /* record each focusable's resting tabindex so off-front gating is reversible */
    for (var ci = 0; ci < cards.length; ci++) {
      var f = cards[ci].querySelectorAll('a, button, input, select, textarea, [tabindex]');
      for (var fj = 0; fj < f.length; fj++) {
        if (!f[fj].hasAttribute('data-kchr-ti0')) {
          f[fj].setAttribute('data-kchr-ti0', f[fj].getAttribute('tabindex') || '');
        }
      }
    }

    var pos = 0, target = 0, wheelVel = 0, lastActive = -1;

    var measuring = false;
    var startX = 0, startY = 0, startTarget = 0, dragging = false, dragged = false, axis = null, activePointer = null, captured = false;

    var motionMql = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var reduceMotion = !!(motionMql && motionMql.matches);
    function onMotionChange(e) { reduceMotion = e.matches; }
    if (motionMql) { try { motionMql.addEventListener('change', onMotionChange); } catch (e) {} }

    function clamp(v) { return Math.max(0, Math.min(p.count - 1, v)); }

    /* opening card, set once. The presets open at their own centre so the fan is
       full on both sides from the first paint; card one alone showed half a fan. */
    pos = target = clamp(Math.round(p.start || 0));
    function norm(a) { a = a % 360; if (a > 180) a -= 360; if (a < -180) a += 360; return a; }

    function gateCard(card, off) {
      var f = card.querySelectorAll('a, button, input, select, textarea, [data-kchr-ti0]');
      for (var i = 0; i < f.length; i++) {
        if (off) {
          f[i].setAttribute('tabindex', '-1');
        } else {
          var orig = f[i].getAttribute('data-kchr-ti0');
          if (orig === '' || orig === null) { f[i].removeAttribute('tabindex'); }
          else { f[i].setAttribute('tabindex', orig); }
        }
      }
    }


    /* ---- motion. Concept moves its sliders with
       transform .6s cubic-bezier(.7, 0, .2, 1) (theme.css), an ease-in-out.
       This engine used exponential decay, which has no ease-in: every step left
       instantly and crawled in, which is the abruptness reported on the
       installed build. This is that curve, evaluated per frame. ---- */
    var KCH_EASE_MS = 600;
    var kchEase = (function () {
      var ax = 0.7, ay = 0.0, bx = 0.2, by = 1.0;
      function A(a, b) { return 1 - 3 * b + 3 * a; }
      function B(a, b) { return 3 * b - 6 * a; }
      function C(a) { return 3 * a; }
      function calc(t, a, b) { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
      function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
      return function (x) {
        if (x <= 0) return 0;
        if (x >= 1) return 1;
        var t = x, i, s, e;
        for (i = 0; i < 8; i++) {
          s = slope(t, ax, bx);
          if (!s) break;
          e = calc(t, ax, bx) - x;
          t -= e / s;
        }
        return calc(t, ay, by);
      };
    })();
    var twFrom = 0, twTo = 0, twT0 = 0, tweening = false;
    function startTween(to) { twFrom = pos; twTo = to; twT0 = 0; tweening = true; }

    /* fan limits. Past 90 degrees sine reverses and a card folds back into the
       fan, landing on or beside a live card. At spread 90 every fourth card
       returned to dead centre with an identical z-index, which is why a
       part-visible card took neither click nor drag. Holding the fan inside
       +-90 makes sin strictly monotonic in d, so no two cards can ever occupy
       the same point. KCH_MAX_FAN only binds at very small spreads. */
    var KCH_HALF_PI = Math.PI / 2;
    var KCH_MAX_FAN = 6;

    /* Scatter offsets are a function of the slot, not of the card.
       Keying them to the card index meant the arrangement changed every time the deck
       advanced, and some combinations put two cards close enough to bunch. Keying them
       to d makes the composition fixed: irregular to look at, identical at every
       position, and verified clear of overlap at every slot. Three sine terms on
       unrelated frequencies give the irregularity; being continuous in d, they also
       stay smooth through a drag instead of jumping as slots change hands. */
    function kchOffX(d) { return Math.sin(d * 2.399 + 1.1); }
    function kchOffY(d) { return Math.sin(d * 1.732 + 0.4); }
    function kchOffS(d) { return Math.sin(d * 2.111 + 2.3); }

    function render() {
      if (root.__kchSuspend) return;
      var spreadRad = p.spread * Math.PI / 180;
      var active = Math.round(pos);
      for (var i = 0; i < cards.length; i++) {
        // pos - i, not i - pos. That one flip mirrors x, rise and tilt
        // together so the next card enters from the trailing side and the
        // stack travels the way every other Concept slider travels. z uses
        // cosine and is unaffected.
        var d = pos - i;
        var theta = d * spreadRad;
        var x = Math.sin(theta) * p.radius + (p.originX - 0.5) * stage.clientWidth;
        var z = (Math.cos(theta) - 1) * p.radius * (p.zflat || 1);
        var y = d * p.rise;
        var tilt = d * p.tilt;
        var card = cards[i];
        var na = Math.abs(norm(tilt));
        // The fold gate keeps sine monotonic so no two cards can share a point. The
        // scatter preset wants the opposite: past 90 degrees the orbit wraps and the
        // card three out swings back to the far side, which is the scattered read.
        // Its window stops before the card four out, which would return to centre.
        var folded = Math.abs(d) > KCH_MAX_FAN || (!p.wrap && Math.abs(theta) > KCH_HALF_PI + 1e-6);
        // Continuous fade instead of a binary cull. The old 0/1 switch popped a card in
        // and out at the 90 degree and depth thresholds, which read as a flicker between
        // cards in cylinder mode and left one-pixel slivers at the spiral's far edge.
        // Each threshold now has a short fade band; geometry past the fold still hides.
        var fadeA = Math.max(0, Math.min(1, (90 - na) / 12));
        var fadeZ = Math.max(0, Math.min(1, (z + p.radius * 1.15) / (p.radius * 0.18)));
        // on-screen card cap: fade out past the visible window so the fan never
        // reaches the arrows, whatever the total review count is
        var half = (p.visible - 1) / 2;
        // The window can be shorter behind than ahead. With a wrapping orbit the card
        // three back returns to the same side as the card one ahead, and the two stack
        // into a cluttered corner; capping the trailing side removes that pair without
        // touching the rest of the field. p.back 0 keeps the window symmetric.
        var lim = (d > 0 && p.back) ? p.back : half;
        var fadeV = Math.max(0, Math.min(1, (lim + 0.5 - Math.abs(d)) / 0.5));
        // continuous depth grade driven by distance from the front slot, not angle.
        // Angle barely moves at cylinder spreads, which is why side cards read at
        // full strength and the front swap had no fade. Distance is continuous
        // through a transition: the outgoing front dims as it leaves the slot while
        // the incoming one brightens into it, a true crossfade, and everything one
        // slot out or further holds at 45%.
        // grade 0: none (scatter). grade 1: opacity crossfade (spiral). grade 2:
        // brightness dim (cylinder): cards overlap flat there, so opacity showed
        // every card through the one in front; brightness recedes without bleed.
        // 23 Jul: the reviews engine now recedes exactly as the carousel does, which is
        // the wireframe's own formula: a gentle opacity step per slot, no filter, no
        // wash and no overlay, so the two sections read as one component.
        var cull = folded ? 0 : Math.min(fadeA, fadeZ, fadeV);
        var recede = Math.max(0.12, 1 - Math.abs(d) * 0.16);
        /* The recede is a wash, not transparency. A review card is always a solid
           panel, so it can be dimmed by laying page colour over it and left fully
           opaque, which looks the same against the page but never lets the card
           behind show through. In cylinder the cards overlap almost completely, and
           that show-through was the smeared look Sean reported. */
        card.style.filter = '';
        card.style.opacity = cull.toFixed(3);
        card.style.setProperty('--kchr-wash', (1 - recede).toFixed(3));
        // shadow strength tapers with depth so an overlapping fan never compounds its
        // shadows into a band; same curve and same base as the carousel.
        var depth = Math.max(0, 1 - Math.abs(d) * 0.28);
        card.style.setProperty('--kchr-shadow-k', (depth * depth).toFixed(3));
        /* only the front card is live, the same gate the carousel uses */
        card.style.pointerEvents = (!folded && Math.abs(d) < 0.5) ? 'auto' : 'none';
        var sc = 1 + Math.max(0, 0.12 * (1 - Math.abs(d)));
        if (p.jitter) {
          var jk = Math.min(Math.abs(d), 1);
          x += kchOffX(d) * p.jitter * jk;
          y += kchOffY(d) * p.jitter * 1.2 * jk;
          sc *= 1 + kchOffS(d) * 0.12 * jk;
        }
        /* stacked in a grid cell: the origin is already the card centre */
        card.style.transform = 'translate3d(' + x.toFixed(2) + 'px, ' + y.toFixed(2) + 'px, ' + z.toFixed(2) + 'px) rotateY(' + tilt.toFixed(2) + 'deg) scale(' + sc.toFixed(3) + ')';
        /* publish this card's live scale so anything inside it that must stay a fixed
           on-screen size can counter it. The cart button uses this: the front card is
           scaled up (sc peaks at 1.12), which would enlarge the button with it, so the
           button divides by this to render at its true 40px. Concept mode never runs
           this loop, so the variable stays unset there and the button's fallback of 1
           keeps it 40px. Sean 25 Jul. */
        card.style.setProperty('--kch-card-scale', sc.toFixed(3));
        card.style.zIndex = String(1000 + Math.round(z));
      }
      if (!measuring && active !== lastActive) {
        lastActive = active;
        for (var k = 0; k < cards.length; k++) {
          var off = (k !== active);
          cards[k].classList.toggle('kch-review-card--active', !off);
          cards[k].setAttribute('aria-hidden', off ? 'true' : 'false');
          gateCard(cards[k], off);
        }
        var lblEl = cards[active] && cards[active].querySelector('.kch-review-card__label');
        statusEl.textContent = statusTpl.replace('[x]', active + 1).replace('[y]', cards.length) + (lblEl ? ': ' + lblEl.textContent.trim() : '');
      }
      syncArrows();
    }

    /* ---- animation loop ---- */
    var raf = null, running = false, lastT = 0;
    var laying = false;
    function frame(ts) {
      if (!lastT) lastT = ts;
      var dtn = Math.min(ts - lastT, 100) / 16.667;
      lastT = ts;
      if (!dragging) {
        if (wheelVel) {
          // a live wheel gesture follows the input directly; the tween takes
          // over once the gesture dies and the carousel snaps to a card
          tweening = false;
          var nt = clamp(target + wheelVel);
          if (nt === target) wheelVel = 0; else target = nt;
          wheelVel *= Math.pow(0.88, dtn);
          if (Math.abs(wheelVel) < 0.0006) { wheelVel = 0; target = clamp(Math.round(target)); startTween(target); }
          else { pos += (target - pos) * (1 - Math.pow(0.80, dtn)); }
        } else if (tweening) {
          if (!twT0) twT0 = ts;
          var k = Math.min(1, (ts - twT0) / KCH_EASE_MS);
          pos = twFrom + (twTo - twFrom) * kchEase(k);
          if (k >= 1) { pos = twTo; tweening = false; }
        } else if (Math.abs(target - pos) > 0.0008) {
          startTween(target);
        }
      }
      var settled = !dragging && !wheelVel && !tweening && Math.abs(target - pos) < 0.0008;
      if (settled) { pos = target; }
      render();
      if (settled) {
        running = false;
        raf = null;
        /* Fit the stage once the motion stops. Fitting during the boot animation
           measured a layout that was still opening, so the stage kept the tighter box
           the first frames produced and the fanned-out cards then hung over its edge.
           Guarded by the same flag that keeps the resize observer from re-entering. */
        /* no refit on settle: the fit is worst-case, so it is the same at every stop */
        return;
      }
      raf = requestAnimationFrame(frame);
    }
    function kick() {
      if (reduceMotion) { wheelVel = 0; pos = target; render(); return; }
      if (!running) { running = true; lastT = 0; raf = requestAnimationFrame(frame); }
    }
    function setTarget(v) { target = clamp(v); if (!reduceMotion) startTween(target); kick(); }

    /* ---- arrows ---- */
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); setTarget(Math.round(target) - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); setTarget(Math.round(target) + 1); });
    function syncArrows() {
      var a = Math.round(target);
      if (prevBtn) prevBtn.toggleAttribute('hidden', a <= 0);
      if (nextBtn) nextBtn.toggleAttribute('hidden', a >= p.count - 1);
    }


    /* Keyboard focus ring, pointer suppression. The theme paints a 2px ring at a 3px
       offset on anything focused (theme.css 3162). After a drag or a tap the browser
       leaves focus on whatever was under the pointer, so that ring appeared around a
       buy button nobody had tabbed to, which is what Sean reported. The root is marked
       while the interaction is a pointer one and unmarked the moment a key is pressed,
       so keyboard users keep their ring intact and pointer users never see it. */
    root.__kchNav = function (dir) { setTarget(Math.round(target) + dir); };

    /* ---- keyboard ----
       One handler only. The stage carried its own arrow handler as well as the
       hover-armed one on the document, so with the pointer over the section and the
       stage focused, both fired and it moved two cards per press. */
    root.__kchHome = function () { setTarget(0); };
    root.__kchEnd = function () { setTarget(cards.length - 1); };

    /* ---- wheel: intentionally none ----
       Removed on Sean's call, 22 Jul. A wheel-driven carousel traps the page scroll
       inside the section and frustrates more than it helps on desktop. Drag, arrows
       and keyboard remain the inputs; the wheelVel variable stays for the tween loop
       and simply never receives input. */

    /* ---- pointer drag (mirrors the hero: setPointerCapture, preventDefault on
       down so the native image drag never steals the pointer) ---- */
    function onDown(e) {
      if (e.button != null && e.button !== 0) return;
      if (e.target.closest('a, button, input, select, textarea, label')) return;
      activePointer = e.pointerId;
      dragging = true; dragged = false; axis = null;
      startX = e.clientX; startY = e.clientY; startTarget = target;
      wheelVel = 0;
      captured = false;
      if (e.cancelable) e.preventDefault();
    }
    function onMove(e) {
      if (!dragging || e.pointerId !== activePointer) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        // biased toward x: thumbs arc, so a swipe that starts slightly vertical is
        // still a swipe. Only a clearly vertical move hands the gesture to the page.
        axis = Math.abs(dx) >= Math.abs(dy) * 0.75 ? 'x' : 'y';
        if (axis === 'y') { dragging = false; releaseCapture(); return; }
        // rebase so the card starts moving from here instead of jumping by the
        // slop distance the instant the axis locks
        startX = e.clientX;
        dx = 0;
        // capture only now: capturing on pointerdown makes the browser retarget
        // the click to the stage, which silently killed every per-card click
        try { stage.setPointerCapture(activePointer); captured = true; } catch (err) { captured = false; }
      }
      if (axis !== 'x') return;
      if (Math.abs(dx) > 3) dragged = true;
      if (e.cancelable) e.preventDefault();
      target = clamp(startTarget - dx / Math.max(60, p.cardW * 0.55));
      pos = target;
      render();
    }
    function releaseCapture() {
      if (captured && activePointer != null) { try { stage.releasePointerCapture(activePointer); } catch (err) {} }
      captured = false;
    }
    function onUp(e) {
      if (e.pointerId !== activePointer) return;
      dragging = false;
      releaseCapture();
      activePointer = null;
      setTarget(Math.round(target));
      if (dragged) { setTimeout(function () { dragged = false; }, 0); }
    }
    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('dragstart', function (e) { e.preventDefault(); });
    // pointer events cannot stop the page scrolling; only a cancelable touchmove
    // can. Once the gesture is ours (axis x), this kills the simultaneous vertical
    // page pan and the pointercancel that was cutting drags short on phones.
    stage.addEventListener('touchmove', function (e) {
      if (dragging && axis === 'x' && e.cancelable) e.preventDefault();
    }, { passive: false });

    /* ---- a click in the empty space beside the cards steps toward that side,
       matching the product carousel. Card clicks are handled per card below;
       this handler ignores anything inside a card or an arrow. ---- */
    function nearCard(x, y) {
      var L = Infinity, R = -Infinity, T = Infinity, B = -Infinity, any = false;
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].style.opacity === '0') continue;
        var r = cards[i].getBoundingClientRect();
        any = true;
        if (r.left < L) L = r.left;
        if (r.right > R) R = r.right;
        if (r.top < T) T = r.top;
        if (r.bottom > B) B = r.bottom;
      }
      return any && x >= L && x <= R && y >= T && y <= B;
    }
    stage.addEventListener('click', function (e) {
      if (dragged) return;
      if (e.target.closest('.kch-review-card') || e.target.closest('.kch-arrow')) return;
      if (!nearCard(e.clientX, e.clientY)) return;
      var r = stage.getBoundingClientRect();
      // sign-aware: with negative spread the left neighbour is the higher
      // index, with positive spread (Scatter) it is the lower one
      setTarget(Math.round(target) + (e.clientX < r.left + r.width / 2 ? -1 : 1) * (p.spread < 0 ? 1 : -1));
    });

    /* ---- per-card wiring: click the active card's photo opens its quick view,
       reusing Concept's quick-view (the eye carries aria-controls). Clicks on a
       real control (eye, product name, buy button) are left alone. ---- */
    cards.forEach(function (card, idx) {
      function isActive() { return Math.round(pos) === idx; }
      card.addEventListener('click', function (e) {
        if (dragged) return;
        if (!isActive()) { setTarget(idx); e.preventDefault(); return; }
        if (e.target.closest('a, button, input, label, select')) return;
        var eye = card.querySelector('.kch-review-card__eye[aria-controls]');
        if (eye) eye.click();
      });
    });

    /* ---- stage height: measure the tallest card (quotes vary) and clip ---- */
    /* The arrows own a no-go zone at each end of the stage, exactly as the concept
       grid reserves one in CSS. The row is measured after layout and the card width
       shrinks until the whole row clears both arrows, so one card is large, six are
       small, and none is ever clipped or tucked under a control. */
    function arrowGutter() {
      /* arrows moved outside the stage on 25 Jul into the card area's reserved padding,
         so there is no gutter to hold back inside the stage. See kch-product-carousel.js
         arrowGutter for the same change. */
      const a = null;
      if (!a || getComputedStyle(a).display === 'none') return 0;
      const r = a.getBoundingClientRect();
      if (!r.width) return 0;
      const sr = stage.getBoundingClientRect();
      return Math.round((r.left - sr.left) + r.width + 16);
    }

    function unionWidth() {
      let l = Infinity, r = -Infinity;
      for (let i = 0; i < cards.length; i++) {
        if (parseFloat(cards[i].style.opacity || '1') < 0.02) continue;
        const b = cards[i].getBoundingClientRect();
        if (!b.width) continue;
        if (b.left < l) l = b.left;
        if (b.right > r) r = b.right;
      }
      return isFinite(l) ? r - l : 0;
    }

    /* The row has to clear the arrows at every scroll position, not only at rest: in an
       orbit the extents grow as cards swing past the front, and at the ends the whole
       fan lies to one side. Same measurement the carousel uses. */
    function worstUnion() {
      var saved = pos, n = cards.length, worst = 0;
      measuring = true;
      for (var i = 0; i < n; i++) {
        var offs = (i < n - 1) ? [0, 0.5] : [0];
        for (var j = 0; j < offs.length; j++) {
          pos = i + offs[j];
          render();
          var u = unionWidth();
          if (u > worst) worst = u;
        }
      }
      pos = saved;
      render();
      measuring = false;
      return worst;
    }

    function fitWidth() {
      if (!cards.length || root.__kchrSuspend) return;
      root.style.removeProperty('--kchr-card-fit');
      readGeometry();
      render();
      const base = cards[0].offsetWidth;
      if (!base) return;
      /* 28px of shadow margin each side, so a card never sits flush against the
         stage's clipping edge and shows its shadow sliced into a hard box */
      const avail = Math.max(120, stage.clientWidth - arrowGutter() * 2 - 56);
      let w = base;
      for (let pass = 0; pass < 4; pass++) {
        const u = worstUnion();
        if (u <= avail || !u) break;
        w = Math.max(110, Math.floor(w * (avail / u) * 0.995));
        root.style.setProperty('--kchr-card-fit', w + 'px');
        readGeometry();
        render();
      }
    }

    /* Fit the stage to what was actually drawn, the same pass the carousel runs. The
       formula above is only a generous provisional so the first render cannot clip;
       this measures the union of the rendered cards and pins the gap above them to a
       constant, which is what stops Spiral opening a canyon under the heading because
       its rise pushes the cards down inside a centred box. */

    /* ---- resize ---- */
    var ro = null;
    /* One pass. The stage takes its height from the in-flow sizer, so there is nothing
       to measure and correct: the first layout is the final one. The only value handed
       back to CSS is the sizer's own height, for the scale allowance, and that comes
       from normal layout so it updates with fonts and images by itself. */
    /* The track is one real card tall, so only the room around it is published, read
       from the boxes the browser paints. One pass, nothing corrected afterwards. */
    function readExtents(h, trackTop, acc) {
      for (var i = 0; i < cards.length; i++) {
        if (parseFloat(cards[i].style.opacity || '1') < 0.02) continue;
        var r = cards[i].getBoundingClientRect();
        var rt = r.top - trackTop;
        var rb = r.bottom - trackTop - h;
        if (rt < acc.up) acc.up = rt;
        if (rb > acc.down) acc.down = rb;
      }
    }

    function publishExtents() {
      if (!cards.length || root.__kchrSuspend) return;
      var h = cards[0].offsetHeight;
      if (!h) return;
      var acc = { up: 0, down: 0 };
      readExtents(h, track.getBoundingClientRect().top, acc);
      /* Worst case across the scroll, not just at rest. A card's painted height depends
         only on its distance from the front, and its offset is that distance times rise,
         so the extreme falls at the half-step between stops. Both are laid out and read
         here, in the same synchronous block, and the resting layout is restored before
         anything is painted. */
      /* The extreme sits somewhere between two stops, and it is not always the midpoint:
         which card is culled changes as the row turns, so the union grows and shrinks
         unevenly across the step. Sampling quarters covers the continuum closely enough
         that nothing clipped anywhere across a full scroll of every card. */
      var saved = pos;
      measuring = true;
      var offsets = [0.25, 0.5, 0.75];
      for (var q = 0; q < offsets.length; q++) {
        pos = saved + offsets[q];
        render();
        readExtents(h, track.getBoundingClientRect().top, acc);
      }
      pos = saved;
      render();
      measuring = false;
      /* Sean 25 Jul: cap the section rather than grow it around the fan.
       *
       * This used to reserve room for the widest extent any card reached anywhere in a
       * full scroll, so spiral, whose rise throws one side high, opened a gap above the
       * cards that concept and cylinder never had. That gap was the only reason a
       * spacing control was needed at all: three effects, three different distances
       * from the heading, and no way to make them agree.
       *
       * The section is now sized for the front card alone. The front card is the one
       * that must be whole; the cards behind it are context and may run past the edge.
       * The stage is already overflow:hidden, so they clip cleanly. Every effect now
       * measures the same from the heading, on every device, with no tuning.
       *
       * Top is a fixed 8px of breathing room. Bottom follows the front card, because
       * its title, price and swatches sit in flow beneath the media and have to fit. */
      var front = track.querySelector('.kch-review-card--active') || cards[0];
      var frontTop = 0, frontBottom = 0;
      if (front) {
        var fr = front.getBoundingClientRect();
        var tt = track.getBoundingClientRect().top;
        /* negative when the card's painted top sits above the track line, which it does
           whenever the active card is scaled up: scale works from the centre, so a card
           at 1.12 lifts its own top by six per cent of its height. A flat 8px ignored
           that and cut the front card's photo, which Sean caught on cylinder where
           nothing else moves vertically at all. */
        frontTop = fr.top - tt;
        frontBottom = fr.bottom - tt - h;
      }
      /* The front card is the one that must be whole, so both paddings follow it and
         nothing else. Cards behind may still run past the edge and clip, which is the
         point: the section stays the same height in every effect. EDGE is the breathing
         room beyond the front card, a little more than concept's zero so a scaled or
         tilted card never touches the frame. */
      var EDGE = 16;
      stage.style.setProperty('--kchr-pad-top', Math.ceil(Math.max(0, -frontTop) + EDGE) + 'px');
      stage.style.setProperty('--kchr-pad-bottom', Math.ceil(Math.max(0, frontBottom) + EDGE) + 'px');
    }
    function relayout() { fitWidth(); syncArrows(); render(); publishExtents(); }
    /* The observer watches the stage, and relayout resizes the stage, so an unguarded
       observer re-enters itself: each pass measured a layout the previous pass had
       already moved, and the fit never settled. That left the top card sitting above
       the stage edge, clipped, which is the canyon-then-crop Sean saw in Spiral. The
       flag makes the section's own resizes invisible to the observer. */
    function relayoutGuarded() {
      if (laying) return;
      laying = true;
      relayout();
      requestAnimationFrame(function () { laying = false; });
    }
    /* A second observer watches a card, not the stage. The stage is sized FROM the
       cards, so the fit is only as good as the card height at the moment it runs, and
       at boot that height is still resolving: images, fonts and the reserved quote slot
       all land later. Measured at 248px during boot against 300px once settled, which
       is exactly the gap that left the top card hanging over the stage edge. This
       re-fits when the card's own size changes, and fitStage does not alter card size,
       so it cannot feed back. */
    var cardRo = null;
    if (window.ResizeObserver && cards[0]) {
      cardRo = new ResizeObserver(function () {
        /* card size changed, fonts or images landing: republish the allowance and
           re-render. No height is written, so nothing jumps. */
        publishExtents();
        render();
      });
      cardRo.observe(cards[0]);
    }

    if (window.ResizeObserver) {
      ro = new ResizeObserver(function () { relayoutGuarded(); });
      ro.observe(stage);
    } else {
      window.addEventListener('resize', relayoutGuarded);
    }

    /* start settled on the first card, ready visuals so no flash of stacked cards */
    /* Measure hidden, then reveal. The fit converges over a few frames and every pass
       moves the cards, so doing it in the open is a visible flicker on every load. The
       track stays hidden while that settles and is shown once. A safety timer clears it
       whatever happens, so the section can never be left invisible. */
    root.classList.add('kch-reviews--ready');
    /* crossing down to phone width after init: park the engine and hand the cards back
       to the stylesheet's swipe row; crossing back up resumes in place. */
    /* named, not inline, so cleanup can remove it. kchMq is a MediaQueryList: it
       outlives the section DOM, so an anonymous handler here survives every editor
       re-render holding a dead root, and they stack up. 27 Jul. */
    const kchCross = (e) => {
      if (e.matches) {
        root.__kchSuspend = true;
        root.classList.remove('kch-reviews--ready');
        cards.forEach((c) => {
          c.style.transform = ''; c.style.opacity = ''; c.style.zIndex = '';
          c.removeAttribute('aria-hidden');
        });
        /* Hand the cards back to Concept's slider. The engine adopted them out of it
           during parse; below 1024px it has no business holding them, and the slider
           cannot drive what it does not contain. Returning them is what makes this a
           real handover rather than the engine simply going quiet: after this the
           section is concept, not a parked engine pretending to be. 27 Jul. */
        kchReturnCards(root);
      } else {
        root.__kchSuspend = false;
        kchAdoptCards(root);
        root.classList.add('kch-reviews--ready');
        render();
      }
    };
    kchMq.addEventListener('change', kchCross);
    requestAnimationFrame(function () {
      relayout();
    });

    root.__kchrCleanup = function () {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      if (cardRo) cardRo.disconnect();
      if (motionMql) { try { motionMql.removeEventListener('change', onMotionChange); } catch (e) {} }
      /* These two are bound to objects that outlive the section, so unless they are
         removed here every editor re-render leaves another copy behind, each closing
         over a root that no longer exists. On the live site sections do not re-render
         so it never showed, which is exactly why it needed looking for rather than
         waiting for. 27 Jul. */
      try { kchMq.removeEventListener('change', kchCross); } catch (e) {}
      window.removeEventListener('resize', relayoutGuarded);
      if (root.__kchrKeys) document.removeEventListener('keydown', root.__kchrKeys);
      /* Hand the cards back and drop the engine class. Cleanup only runs on unload,
         where the DOM is discarded, so nothing breaks today without this. It is here
         because a teardown that leaves the class asserting one thing while the DOM
         holds another is the exact drift that cost a day: state and reality must not
         be allowed to disagree, even briefly, even where nothing currently looks.
         Found by mutation testing, 27 Jul. */
      try { kchReturnCards(root); } catch (e) {}
    };
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-kch-reviews]');
    Array.prototype.forEach.call(roots, initReviews);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Shopify theme-editor lifecycle */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-kch-reviews]');
    if (root && root.__kchrCleanup) root.__kchrCleanup();
  });
})();
